import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Calculator } from "lucide-react";

export default function PricingCalculator() {
  const [f, setF] = useState({ direct_cost: 1000000, indirect_cost: 150000, overhead_pct: 0.1, margin_pct: 0.12, contingency_pct: 0.05, tax_pct: 0 });
  const [result, setResult] = useState(null);

  const calc = async () => {
    try {
      const payload = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, parseFloat(v) || 0]));
      const { data } = await api.post("/pricing/calculate", payload);
      setResult(data);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  const input = (k, label, isPct = false, step = "any") => (
    <div>
      <label className="stat-label block mb-1.5">{label}{isPct ? " (0-1)" : ""}</label>
      <input type="number" step={step} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
        data-testid={`calc-${k}`}
        className="w-full px-3 py-2 border border-ink-line bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand font-num" />
    </div>
  );

  const fmt = (n) => Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });

  return (
    <div className="space-y-6 max-w-5xl" data-testid="pricing-page">
      <div className="border-b border-ink-line pb-6">
        <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Pricing Intelligence</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Pricing Calculator</h1>
        <p className="text-sm text-ink-muted mt-2">Model direct and indirect costs, overhead, margin, contingency and tax to derive your bid price.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-flat p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Calculator className="w-4 h-4 text-brand" />
            <div className="font-display font-semibold tracking-tight">Inputs</div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {input("direct_cost", "Direct Cost")}
            {input("indirect_cost", "Indirect Cost")}
            {input("overhead_pct", "Overhead %", true, "0.01")}
            {input("margin_pct", "Margin %", true, "0.01")}
            {input("contingency_pct", "Contingency %", true, "0.01")}
            {input("tax_pct", "Tax %", true, "0.01")}
          </div>
          <button onClick={calc} data-testid="calc-run-btn" className="w-full bg-brand hover:bg-brand-hover text-white py-2.5 text-sm font-medium rounded-sm transition-colors">
            Calculate Bid Price
          </button>
        </div>

        <div className="card-flat p-6">
          <div className="stat-label">Estimated Bid Price</div>
          <div className="font-num text-5xl font-bold text-brand mt-3" data-testid="calc-total">
            {result ? fmt(result.total_bid_price) : "—"}
          </div>
          <div className="text-[11px] uppercase tracking-wider text-ink-muted mt-2">
            Effective margin <span className="font-num text-ink font-medium">{result ? result.effective_margin_pct : 0}%</span>
          </div>

          <div className="mt-6 pt-6 border-t border-ink-line divide-y divide-ink-line text-sm">
            {result && [
              ["Base Cost", result.base],
              ["+ Overhead", result.overhead],
              ["+ Contingency", result.contingency],
              ["+ Margin", result.margin],
              ["+ Tax", result.tax],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between py-2">
                <div className="text-ink-muted">{k}</div>
                <div className="font-num font-medium">{fmt(v)}</div>
              </div>
            ))}
            {result && (
              <div className="flex items-center justify-between py-3 bg-brand-soft -mx-6 px-6 mt-2">
                <div className="font-display font-bold">Total Bid Price</div>
                <div className="font-num font-bold text-lg text-brand">{fmt(result.total_bid_price)}</div>
              </div>
            )}
            {!result && <div className="text-sm text-ink-muted py-6">Fill the inputs and click Calculate.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
